import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authApi } from '@/services/api';
import { toast } from 'react-hot-toast';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import type { PasswordChangeRequest, AuthInfoResponse } from '@/types/auth';

// Password validation schema for changing password
const passwordSchema = z.object({
  current_password: z.string().min(1, 'Current password is required'),
  new_password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/\d/, 'Password must contain at least one digit'),
  confirm_password: z.string().min(1, 'Please confirm your new password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

// Password validation schema for setting password (Google-only users)
const setPasswordSchema = z.object({
  new_password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/\d/, 'Password must contain at least one digit'),
  confirm_password: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.new_password === data.confirm_password, {
  message: 'Passwords do not match',
  path: ['confirm_password'],
});

type PasswordFormData = z.infer<typeof passwordSchema>;
type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

export function SecuritySettings() {
  const queryClient = useQueryClient();
  const [showUnlinkConfirm, setShowUnlinkConfirm] = useState(false);

  // Fetch auth info to know if user has password and/or Google linked
  // refetchOnWindowFocus ensures we get fresh data if user verified email in another tab
  const { data: authInfo, isLoading: isLoadingAuthInfo } = useQuery<AuthInfoResponse>({
    queryKey: ['auth-info'],
    queryFn: () => authApi.getAuthInfo(),
    refetchOnWindowFocus: true,
    staleTime: 0, // Always consider data stale so it refetches on mount
  });

  // Change password form
  const {
    register: registerChange,
    handleSubmit: handleSubmitChange,
    reset: resetChange,
    watch: watchChange,
    formState: { errors: changeErrors, isValid: isChangeValid },
  } = useForm<PasswordFormData>({
    resolver: zodResolver(passwordSchema),
    mode: 'onChange',
  });

  // Set password form (for Google-only users)
  const {
    register: registerSet,
    handleSubmit: handleSubmitSet,
    reset: resetSet,
    watch: watchSet,
    formState: { errors: setErrors, isValid: isSetValid },
  } = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    mode: 'onChange',
  });

  const newPasswordChange = watchChange('new_password', '');
  const newPasswordSet = watchSet('new_password', '');

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/\d/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    return strength;
  };

  const strengthLabels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  const strengthColors = ['bg-red-500', 'bg-orange-500', 'bg-yellow-500', 'bg-blue-500', 'bg-green-500'];

  // Change password mutation
  const changeMutation = useMutation({
    mutationFn: (data: PasswordChangeRequest) => authApi.changePassword(data),
    onSuccess: () => {
      toast.success('Password changed successfully');
      resetChange();
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to change password');
    },
  });

  // Set password mutation (for Google-only users)
  const setPasswordMutation = useMutation({
    mutationFn: (data: SetPasswordFormData) => authApi.setPassword(data),
    onSuccess: () => {
      toast.success('Password set successfully! You can now sign in with email/password.');
      resetSet();
      queryClient.invalidateQueries({ queryKey: ['auth-info'] });
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to set password');
    },
  });

  // Unlink Google account mutation
  const unlinkMutation = useMutation({
    mutationFn: () => authApi.googleUnlinkAccount(),
    onSuccess: () => {
      toast.success('Google account unlinked successfully');
      queryClient.invalidateQueries({ queryKey: ['auth-info'] });
      setShowUnlinkConfirm(false);
    },
    onError: (error: unknown) => {
      const apiError = error as { response?: { data?: { detail?: string } } };
      toast.error(apiError.response?.data?.detail || 'Failed to unlink Google account');
    },
  });

  const onSubmitChange = (data: PasswordFormData) => {
    changeMutation.mutate(data);
  };

  const onSubmitSet = (data: SetPasswordFormData) => {
    setPasswordMutation.mutate(data);
  };

  // Handle Google link success
  const handleGoogleLinkSuccess = () => {
    toast.success('Google account linked successfully!');
    queryClient.invalidateQueries({ queryKey: ['auth-info'] });
  };

  // Handle Google link error
  const handleGoogleLinkError = (error: string) => {
    toast.error(error);
  };

  // Render password strength indicator
  const renderPasswordStrength = (password: string) => {
    const passwordStrength = getPasswordStrength(password);
    return (
      <div className="mt-2">
        <div className="flex gap-1 mb-1">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded ${
                i < passwordStrength ? strengthColors[passwordStrength - 1] : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <p className={`text-xs ${
          passwordStrength <= 2 ? 'text-red-600' :
          passwordStrength <= 3 ? 'text-yellow-600' : 'text-green-600'
        }`}>
          Password strength: {strengthLabels[passwordStrength - 1] || 'Very Weak'}
        </p>
      </div>
    );
  };

  const hasPassword = authInfo?.has_password ?? true;
  const hasGoogleLinked = authInfo?.google_linked ?? false;
  const canUnlinkGoogle = hasGoogleLinked && hasPassword;

  if (isLoadingAuthInfo) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" variant="primary" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-6">Security Settings</h2>

      {/* Sign-in Methods Section */}
      <div className="mb-8">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Sign-in Methods</h3>
        <div className="bg-gray-50 border border-gray-200 rounded-lg divide-y divide-gray-200">
          {/* Password Method */}
          <div className="p-4 flex items-center gap-3">
            <div className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center">
              <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <p className="font-medium text-gray-900">Password</p>
              <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                hasPassword ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>
                {hasPassword ? 'Active' : 'Not yet set'}
              </div>
            </div>
          </div>

          {/* Google Method */}
          <div className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-white border border-gray-300 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <div>
                <p className="font-medium text-gray-900">Google</p>
                <div className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                  hasGoogleLinked ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                }`}>
                  {hasGoogleLinked ? 'Linked' : 'Not linked'}
                </div>
              </div>
            </div>
            {hasGoogleLinked ? (
              canUnlinkGoogle && (
                <button
                  onClick={() => setShowUnlinkConfirm(true)}
                  className="px-3 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-red-100 hover:border-red-300 transition-colors"
                >
                  Unlink
                </button>
              )
            ) : (
              <GoogleSignInButton
                mode="link"
                onSuccess={handleGoogleLinkSuccess}
                onError={handleGoogleLinkError}
                width={150}
              />
            )}
          </div>
        </div>
      </div>

      {/* Password Section - Show appropriate form based on whether user has password */}
      {hasPassword ? (
        /* Change Password Form */
        <form onSubmit={handleSubmitChange(onSubmitChange)} className="space-y-6 max-w-md">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Change Password</h3>
            <p className="text-sm text-gray-600 mb-6">
              Choose a strong password with at least 8 characters, including uppercase, lowercase, and numbers.
            </p>
          </div>

          {/* Current Password */}
          <div>
            <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-1">
              Current Password
            </label>
            <input
              type="password"
              id="current_password"
              {...registerChange('current_password')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                changeErrors.current_password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {changeErrors.current_password && (
              <p className="mt-1 text-sm text-red-600">{changeErrors.current_password.message}</p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-1">
              New Password
            </label>
            <input
              type="password"
              id="new_password"
              {...registerChange('new_password')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                changeErrors.new_password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {changeErrors.new_password && (
              <p className="mt-1 text-sm text-red-600">{changeErrors.new_password.message}</p>
            )}
            {newPasswordChange && renderPasswordStrength(newPasswordChange)}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirm_password"
              {...registerChange('confirm_password')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                changeErrors.confirm_password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {changeErrors.confirm_password && (
              <p className="mt-1 text-sm text-red-600">{changeErrors.confirm_password.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={!isChangeValid || changeMutation.isPending}
              className={`w-full px-6 py-2 rounded-md font-medium transition-colors ${
                isChangeValid && !changeMutation.isPending
                  ? 'bg-blue-700 text-white hover:bg-blue-800'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {changeMutation.isPending ? 'Changing Password...' : 'Change Password'}
            </button>
          </div>
        </form>
      ) : (
        /* Set Password Form (for Google-only users) */
        <form onSubmit={handleSubmitSet(onSubmitSet)} className="space-y-6 max-w-md">
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Set Password</h3>
            <p className="text-sm text-gray-600 mb-6">
              You signed up with Google and have yet to set a password.
            </p>
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="set_new_password" className="block text-sm font-medium text-gray-700 mb-1">
              Password
            </label>
            <input
              type="password"
              id="set_new_password"
              {...registerSet('new_password')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                setErrors.new_password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {setErrors.new_password && (
              <p className="mt-1 text-sm text-red-600">{setErrors.new_password.message}</p>
            )}
            {newPasswordSet && renderPasswordStrength(newPasswordSet)}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="set_confirm_password" className="block text-sm font-medium text-gray-700 mb-1">
              Confirm Password
            </label>
            <input
              type="password"
              id="set_confirm_password"
              {...registerSet('confirm_password')}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                setErrors.confirm_password ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {setErrors.confirm_password && (
              <p className="mt-1 text-sm text-red-600">{setErrors.confirm_password.message}</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={!isSetValid || setPasswordMutation.isPending}
              className={`w-full px-6 py-2 rounded-md font-medium transition-colors ${
                isSetValid && !setPasswordMutation.isPending
                  ? 'bg-blue-700 text-white hover:bg-blue-800'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {setPasswordMutation.isPending ? 'Setting Password...' : 'Set Password'}
            </button>
          </div>
        </form>
      )}

      {/* Security Tips */}
      <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg max-w-md">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Security Tips</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>Use a unique password that you don't use for other accounts</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>Consider using a password manager to generate and store strong passwords</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 mt-0.5">•</span>
            <span>Never share your password with anyone</span>
          </li>
          {hasGoogleLinked && (
            <li className="flex items-start gap-2">
              <span className="text-blue-600 mt-0.5">•</span>
              <span>Having both password and Google linked provides backup sign-in options</span>
            </li>
          )}
        </ul>
      </div>

      {/* Unlink Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showUnlinkConfirm}
        onClose={() => setShowUnlinkConfirm(false)}
        onConfirm={() => unlinkMutation.mutate()}
        title="Unlink Google Account"
        message="Are you sure you want to unlink your Google account? You will no longer be able to sign in with Google."
        confirmText="Unlink"
        cancelText="Cancel"
        variant="warning"
        isLoading={unlinkMutation.isPending}
      />
    </div>
  );
}
